/**
 * Stripe Configuration Component
 *
 * Allows system admins to configure Stripe API keys, price IDs, and billing settings.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { BillingPlansManager } from './BillingPlansManager';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface StripeConfig {
  stripeSecretKey: string | null;
  stripePublishableKey: string | null;
  stripeWebhookSecret: string | null;
  stripeStandardPriceId: string | null;
  stripeNJAPPriceId: string | null;
  stripePlatformPriceId: string | null;
  billingGracePeriodDays: number;
  trialPeriodDays: number;
}

export const StripeConfiguration: React.FC = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState<StripeConfig>({
    stripeSecretKey: null,
    stripePublishableKey: null,
    stripeWebhookSecret: null,
    stripeStandardPriceId: null,
    stripeNJAPPriceId: null,
    stripePlatformPriceId: null,
    billingGracePeriodDays: 30,
    trialPeriodDays: 21,
  });

  const [formData, setFormData] = useState<Partial<StripeConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'plans'>('config');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      const response = await axios.get(`${API_BASE_URL}/api/system-admin/settings/stripe-config`, config);
      setConfig(response.data);
      // Initialize form with current values (masked keys will be empty)
      setFormData({
        billingGracePeriodDays: response.data.billingGracePeriodDays,
        trialPeriodDays: response.data.trialPeriodDays,
        stripeStandardPriceId: response.data.stripeStandardPriceId,
        stripeNJAPPriceId: response.data.stripeNJAPPriceId,
        stripePlatformPriceId: response.data.stripePlatformPriceId,
      });
    } catch (error: any) {
      console.error('Error fetching Stripe config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load configuration'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof StripeConfig, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      await axios.post(
        `${API_BASE_URL}/api/system-admin/settings/stripe-config`,
        {
          stripeSecretKey: formData.stripeSecretKey || undefined,
          stripePublishableKey: formData.stripePublishableKey || undefined,
          stripeWebhookSecret: formData.stripeWebhookSecret || undefined,
          stripeStandardPriceId: formData.stripeStandardPriceId,
          stripeNJAPPriceId: formData.stripeNJAPPriceId,
          stripePlatformPriceId: formData.stripePlatformPriceId,
          billingGracePeriodDays: formData.billingGracePeriodDays,
          trialPeriodDays: formData.trialPeriodDays,
        },
        config
      );

      setMessage({
        type: 'success',
        text: 'Stripe configuration saved successfully!'
      });

      // Reload config
      await fetchConfig();
    } catch (error: any) {
      console.error('Error saving Stripe config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save configuration'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setMessage(null);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await axios.post(`${API_BASE_URL}/api/system-admin/settings/stripe-config/test`, {}, config);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Stripe connection test successful!'
        });
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Connection test failed'
        });
      }
    } catch (error: any) {
      console.error('Error testing Stripe connection:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600 dark:text-gray-400">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'config'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            API Configuration
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'plans'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Rate Tables
          </button>
        </div>
      </div>

      {activeTab === 'plans' ? (
        <BillingPlansManager token={token || ''} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Stripe API Configuration
          </h2>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* API Keys Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              API Keys
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  placeholder={config.stripeSecretKey || 'sk_live_...'}
                  value={formData.stripeSecretKey || ''}
                  onChange={(e) => handleInputChange('stripeSecretKey', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {config.stripeSecretKey
                    ? <>Saved key: <span className="font-mono">{config.stripeSecretKey}</span> ({config.stripeSecretKey.startsWith('sk_live_') || config.stripeSecretKey.startsWith('rk_live_') ? 'LIVE mode' : config.stripeSecretKey.includes('_test_') ? 'TEST mode' : 'mode unknown'}). Leave blank to keep it.</>
                    : 'No secret key saved.'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Publishable Key
                </label>
                <input
                  type="text"
                  placeholder={config.stripePublishableKey || 'pk_live_...'}
                  value={formData.stripePublishableKey || ''}
                  onChange={(e) => handleInputChange('stripePublishableKey', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Webhook Secret
                </label>
                <input
                  type="password"
                  placeholder={config.stripeWebhookSecret || 'whsec_...'}
                  value={formData.stripeWebhookSecret || ''}
                  onChange={(e) => handleInputChange('stripeWebhookSecret', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Price IDs Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Price Configuration
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Platform Price ID (V2)
                </label>
                <input
                  type="text"
                  placeholder="price_..."
                  value={formData.stripePlatformPriceId || ''}
                  onChange={(e) => handleInputChange('stripePlatformPriceId', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  $0/month placeholder Price used for V2 billing. Actual charges are calculated by the pricing engine and added as invoice line items.
                </p>
              </div>

              {/* Legacy Price IDs - collapsible */}
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  Legacy Price IDs (V1 - for migration only)
                </summary>
                <div className="mt-3 space-y-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Standard Plan Price ID
                    </label>
                    <input
                      type="text"
                      placeholder="price_..."
                      value={formData.stripeStandardPriceId || ''}
                      onChange={(e) => handleInputChange('stripeStandardPriceId', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      NJAP Plan Price ID
                    </label>
                    <input
                      type="text"
                      placeholder="price_..."
                      value={formData.stripeNJAPPriceId || ''}
                      onChange={(e) => handleInputChange('stripeNJAPPriceId', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* Billing Settings Section */}
          <div className="pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Billing Settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trial Period (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="90"
                  value={formData.trialPeriodDays || 21}
                  onChange={(e) => handleInputChange('trialPeriodDays', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Default: 21 days (3 weeks)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Grace Period (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="90"
                  value={formData.billingGracePeriodDays || 30}
                  onChange={(e) => handleInputChange('billingGracePeriodDays', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Days to allow after payment failure before account lockout
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>

            <button
              onClick={handleTestConnection}
              disabled={testing || !config.stripeSecretKey}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
            Configuration Guide
          </h4>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
            <li>• Get your API keys from the Stripe Dashboard</li>
            <li>• Use test keys (sk_test_...) for development</li>
            <li>• Use live keys (sk_live_...) for production</li>
            <li>• Create webhook endpoint at: /api/webhooks/stripe</li>
            <li>• Price IDs are found in your Stripe Products dashboard</li>
          </ul>
        </div>
        </div>
      )}
    </div>
  );
};
